import { AlertCircle, Loader2, RefreshCw, Search } from "lucide-react";
import { CONTACTS_PRIMARY_BUTTON_CLASS } from "../../../pages/whatsapp/data/contactsData";
import PaginationComponent from "../../shared/panel/Pagination";
import { Badge } from "../../tailwind/badge";
import { Button } from "../../tailwind/button";
import { Input } from "../../tailwind/input";
import { getStatusColor } from "../../../pages/whatsapp/utils/contactsUtils";
import { WaBone } from "../../../pages/whatsapp/components/skeletons/WhatsAppPageSkeletons";

const TABLE_HEADERS = [
  { key: "name", label: "Name", className: "" },
  { key: "phone", label: "Phone", className: "" },
  { key: "email", label: "Email", className: "hidden lg:table-cell" },
  { key: "leadStatus", label: "Lead Status", className: "" },
  { key: "source", label: "Source", className: "hidden md:table-cell" },
  { key: "tags", label: "Tags", className: "hidden xl:table-cell" },
  { key: "lastActive", label: "Last Active", className: "hidden sm:table-cell" },
];

function ContactTags({ tags }) {
  const safeTags = tags || [];
  if (!safeTags.length) {
    return <span className="text-gray-400 dark:text-gray-500 text-sm">-</span>;
  }

  return (
    <div className="flex flex-wrap gap-1 min-w-0">
      {safeTags.slice(0, 2).map((tag) => (
        <Badge
          key={tag}
          variant="secondary"
          title={tag}
          className="font-normal text-[10px] max-w-[8rem] truncate bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-gray-300"
        >
          {tag}
        </Badge>
      ))}
      {safeTags.length > 2 && (
        <Badge
          variant="secondary"
          className="font-normal text-[10px] shrink-0 bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-gray-300"
        >
          +{safeTags.length - 2}
        </Badge>
      )}
    </div>
  );
}

function LoadingSkeletonRows() {
  return Array.from({ length: 5 }).map((_, index) => (
    <tr key={`skeleton-${index}`} className="animate-pulse">
      {TABLE_HEADERS.map((header) => (
        <td
          key={header.key}
          className={`py-3 px-3 sm:px-4 ${header.className}`}
        >
          <WaBone className="h-4 w-full max-w-[120px]" />
        </td>
      ))}
    </tr>
  ));
}

function ContactCard({ contact, onViewContact }) {
  return (
    <div
      className="rounded-lg border border-gray-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 space-y-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-800/50 transition-colors"
      onClick={() => onViewContact(contact)}
    >
      <div className="flex items-start justify-between gap-3 min-w-0">
        <div className="min-w-0 flex-1">
          <p className="font-medium text-sm text-gray-900 dark:text-white truncate">
            {contact.name || "—"}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-300 truncate">
            {contact.phone || "—"}
          </p>
        </div>
        <Badge
          variant="outline"
          className={`font-normal text-[11px] px-2 py-0.5 shrink-0 max-w-[40%] truncate ${getStatusColor(
            contact.leadStatus
          )}`}
          title={contact.leadStatus || undefined}
        >
          {contact.leadStatus || "—"}
        </Badge>
      </div>

      <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-xs text-gray-600 dark:text-gray-300">
        <div className="min-w-0">
          <span className="text-gray-400 dark:text-gray-500 block">Email</span>
          <span className="truncate block" title={contact.email || undefined}>
            {contact.email || "—"}
          </span>
        </div>
        <div className="min-w-0">
          <span className="text-gray-400 dark:text-gray-500 block">Source</span>
          <span className="truncate block">{contact.source || "—"}</span>
        </div>
        <div className="min-w-0">
          <span className="text-gray-400 dark:text-gray-500 block">Last active</span>
          <span className="truncate block">{contact.lastActiveAt || "—"}</span>
        </div>
        <div className="min-w-0">
          <span className="text-gray-400 dark:text-gray-500 block mb-1">Tags</span>
          <ContactTags tags={contact.tags} />
        </div>
      </div>
    </div>
  );
}

export default function ContactsTable({
  contacts,
  searchQuery,
  loading = false,
  loaded = false,
  error = null,
  currentPage = 1,
  totalPages = 1,
  availablePages = [1],
  hasNextPage = false,
  showPagination = false,
  onPageChange,
  onPrev,
  onNext,
  onRetry,
  onSearchChange,
  onSearchSubmit,
  onSearchClear,
  hasAppliedSearch = false,
  onViewContact,
}) {
  const showInitialLoading = loading && !loaded;
  const showEmpty = loaded && !loading && !error && contacts.length === 0;
  const showError = loaded && error && contacts.length === 0;
  const canClearSearch = Boolean(searchQuery.trim()) || hasAppliedSearch;

  return (
    <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-800 shadow-sm overflow-hidden">
      <div className="p-3 sm:p-4 border-b border-gray-200 dark:border-slate-800 flex flex-col gap-3 bg-gray-50/50 dark:bg-slate-950/50">
        <div className="flex flex-col sm:flex-row w-full sm:max-w-xl gap-2">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
            <Input
              placeholder="Search by name, email or phone..."
              name="contacts-table-search"
              autoComplete="off"
              className="pl-9 w-full h-9 bg-white dark:bg-slate-950 border-gray-200 dark:border-slate-800"
              value={searchQuery}
              onChange={(event) => onSearchChange(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  onSearchSubmit?.();
                }
              }}
              disabled={showInitialLoading}
            />
          </div>
          <div className="flex gap-2 shrink-0 w-full sm:w-auto">
            <Button
              type="button"
              className={`h-9 flex-1 sm:flex-none ${CONTACTS_PRIMARY_BUTTON_CLASS}`}
              onClick={onSearchSubmit}
              disabled={showInitialLoading || loading}
            >
              Search
            </Button>
            {onSearchClear && (
              <Button
                type="button"
                variant="outline"
                className="h-9 flex-1 sm:flex-none border-gray-200 dark:border-slate-700"
                onClick={onSearchClear}
                disabled={showInitialLoading || loading || !canClearSearch}
              >
                Clear
              </Button>
            )}
          </div>
        </div>
        {loading && loaded && (
          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Refreshing…
          </div>
        )}
      </div>

      {showError && (
        <div className="mx-3 sm:mx-4 mt-4 flex flex-col sm:flex-row sm:items-center gap-3 rounded-lg border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-950/30 p-4">
          <div className="flex items-start gap-2 text-sm text-red-700 dark:text-red-300 flex-1">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
          {onRetry && (
            <Button
              variant="outline"
              size="sm"
              className="shrink-0 border-red-200 dark:border-red-800 text-red-700 dark:text-red-300"
              onClick={onRetry}
            >
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
              Retry
            </Button>
          )}
        </div>
      )}

      {/* Mobile cards — same fields as table; tap opens profile (no Actions column) */}
      <div className="md:hidden p-3 space-y-3">
        {showInitialLoading &&
          Array.from({ length: 3 }).map((_, index) => (
            <div
              key={`card-skeleton-${index}`}
              className="rounded-lg border border-gray-200 dark:border-slate-800 p-4 space-y-3"
            >
              <WaBone className="h-4 w-2/3" />
              <WaBone className="h-3 w-1/2" />
              <WaBone className="h-8 w-full" />
            </div>
          ))}
        {!showInitialLoading &&
          !showError &&
          contacts.map((contact) => (
            <ContactCard
              key={contact.id}
              contact={contact}
              onViewContact={onViewContact}
            />
          ))}
        {showEmpty && (
          <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
            No contacts found matching your criteria.
          </p>
        )}
      </div>

      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full min-w-[720px]">
          <thead>
            <tr className="bg-gray-50 dark:bg-slate-950 border-b border-gray-200 dark:border-slate-800">
              {TABLE_HEADERS.map((header) => (
                <th
                  key={header.key}
                  className={`py-3 px-3 sm:px-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap ${header.className || "text-left"}`}
                >
                  {header.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
            {showInitialLoading && <LoadingSkeletonRows />}
            {!showInitialLoading &&
              !showError &&
              contacts.map((contact) => (
                <tr
                  key={contact.id}
                  className="hover:bg-gray-50 dark:hover:bg-slate-800/50 transition-colors cursor-pointer"
                  onClick={() => onViewContact(contact)}
                >
                  <td className="py-3 px-3 sm:px-4">
                    <div className="font-medium text-sm text-gray-900 dark:text-white truncate max-w-[160px]">
                      {contact.name || "—"}
                    </div>
                  </td>
                  <td className="py-3 px-3 sm:px-4 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                    {contact.phone || "—"}
                  </td>
                  <td className="py-3 px-3 sm:px-4 text-sm text-gray-600 dark:text-gray-300 hidden lg:table-cell truncate max-w-[180px]">
                    {contact.email || "—"}
                  </td>
                  <td className="py-3 px-3 sm:px-4">
                    <Badge
                      variant="outline"
                      className={`font-normal text-[11px] px-2 py-0.5 ${getStatusColor(
                        contact.leadStatus
                      )}`}
                    >
                      {contact.leadStatus || "—"}
                    </Badge>
                  </td>
                  <td className="py-3 px-3 sm:px-4 text-sm text-gray-600 dark:text-gray-300 hidden md:table-cell truncate max-w-[140px]">
                    {contact.source || "—"}
                  </td>
                  <td className="py-3 px-3 sm:px-4 hidden xl:table-cell">
                    <ContactTags tags={contact.tags} />
                  </td>
                  <td className="py-3 px-3 sm:px-4 text-sm text-gray-600 dark:text-gray-300 hidden sm:table-cell whitespace-nowrap">
                    {contact.lastActiveAt || "—"}
                  </td>
                </tr>
              ))}
            {showEmpty && (
              <tr>
                <td
                  colSpan={TABLE_HEADERS.length}
                  className="py-8 text-center text-sm text-gray-500 dark:text-gray-400"
                >
                  No contacts found matching your criteria.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showPagination && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-3 sm:px-4 py-4 border-t border-gray-200 dark:border-slate-800 bg-gray-50/50 dark:bg-slate-950/50">
          <p className="text-xs text-gray-500 dark:text-gray-400 order-2 sm:order-1">
            Page {currentPage}
            {contacts.length > 0 && ` · ${contacts.length} contact${contacts.length === 1 ? "" : "s"}`}
          </p>
          <div className="order-1 sm:order-2 overflow-x-auto max-w-full">
            <PaginationComponent
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={onPageChange}
              onPrev={onPrev}
              onNext={onNext}
              availablePages={availablePages}
              hasNextToken={hasNextPage}
              size="sm"
            />
          </div>
        </div>
      )}
    </div>
  );
}
